import classNames from 'classnames';
import { Children, type ReactNode, isValidElement } from 'react';

import { Box, type BoxProps } from '../../components/Box/Box';
import * as styles from './SplitLayout.css';

type SidePosition = 'left' | 'right';

interface SplitLayoutProps {
  /** Which side of the viewport the Side pane sits on. Defaults to `left`. */
  sidePosition?: SidePosition;
  className?: string;
  children: ReactNode;
}

interface SplitLayoutSlotProps extends BoxProps {
  children: ReactNode;
}

interface SplitLayoutFooterProps {
  leading?: ReactNode;
  center?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}

/**
 * Two-pane split layout. Renders a `Side` pane and a `Main` pane next to
 * each other above the `tablet` breakpoint; below it, the side pane is
 * hidden and the main pane takes the full width.
 *
 * Rebrand chrome is applied by default: cream background on the root +
 * side, white card-white main pane with a 40px inside-edge corner radius,
 * viewport-locked sizing.
 *
 * `SplitLayout.Logo` and `SplitLayout.Footer` are children of whichever
 * pane they belong to (typically `.Main`) — they render inline within the
 * pane's flex column, with `Footer` sticky at the bottom of the scrolling
 * pane.
 *
 * Side / Main ordering is decided by `sidePosition`, not by the order the
 * consumer passes them as children — so callers can write whichever order
 * reads best for them.
 *
 * @example
 *   <SplitLayout sidePosition="right">
 *     <SplitLayout.Main>
 *       <SplitLayout.Logo>
 *         <BlinqLogo />
 *       </SplitLayout.Logo>
 *       <FormCenter>{form}</FormCenter>
 *       <SplitLayout.Footer center={<ProgressPips currentStep={1} totalSteps={4} />} />
 *     </SplitLayout.Main>
 *     <SplitLayout.Side>{illustration}</SplitLayout.Side>
 *   </SplitLayout>
 */
export const SplitLayout = ({
  sidePosition = 'left',
  className,
  children,
}: SplitLayoutProps): JSX.Element => {
  const childArray = Children.toArray(children);
  const sideEl = childArray.find((child) => isValidElement(child) && child.type === Side);
  const mainEl = childArray.find((child) => isValidElement(child) && child.type === Main);
  const otherEls = childArray.filter((child) => child !== sideEl && child !== mainEl);

  // Side sits on the requested side regardless of how the consumer ordered
  // the children. `sidePosition='left'` → Side first; otherwise Main first.
  const orderedPanes =
    sidePosition === 'left' ? ([sideEl, mainEl] as const) : ([mainEl, sideEl] as const);

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.contentRow} data-side-position={sidePosition}>
        {orderedPanes}
        {otherEls}
      </div>
    </div>
  );
};

const Side = ({ className, children, ...boxProps }: SplitLayoutSlotProps): JSX.Element => (
  <Box className={classNames(styles.side, className)} {...boxProps}>
    {children}
  </Box>
);
Side.displayName = 'SplitLayout.Side';

const Main = ({ className, children, ...boxProps }: SplitLayoutSlotProps): JSX.Element => (
  <Box className={classNames(styles.main, className)} {...boxProps}>
    {children}
  </Box>
);
Main.displayName = 'SplitLayout.Main';

const Logo = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}): JSX.Element => <div className={classNames(styles.logoContainer, className)}>{children}</div>;
Logo.displayName = 'SplitLayout.Logo';

const Footer = ({ leading, center, trailing, className }: SplitLayoutFooterProps): JSX.Element => (
  <div className={classNames(styles.footer, className)}>
    <div className={styles.footerSlot.leading}>{leading}</div>
    <div className={styles.footerSlot.center}>{center}</div>
    <div className={styles.footerSlot.trailing}>{trailing}</div>
  </div>
);
Footer.displayName = 'SplitLayout.Footer';

SplitLayout.Side = Side;
SplitLayout.Main = Main;
SplitLayout.Logo = Logo;
SplitLayout.Footer = Footer;

export default SplitLayout;
